import { FC } from "react";

import { Microsoft365Colored } from "@livechat/design-system-icons";
import { Alert, Icon, Link, SpacingToken, Text } from "@livechat/design-system-react-components";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import Block from "components/layout/Block";
import { HelpCenter } from "constants/help-center";
import { IProviderItemProps } from "interfaces/providerItem";
import { withVar } from "themes/helpers";

import { ForwardingAddressCopy } from "./ForwardingAddressCopy";
import { LiWithMargin, StepsList } from "./styles";
import { onHelpCenterLinkClick } from "./trackProviderEngagement";

interface IProps {
  onSendForwardingClick: () => void;
}

export const MS365Provider: FC<IProps & IProviderItemProps> = ({
  opened,
  onHeaderClick,
  inboxAddress,
  handleMailboxCopying,
  onSendForwardingClick,
}) => (
  <AccordionItem
    opened={opened}
    onHeaderClick={onHeaderClick}
    title="Microsoft 365"
    icon={<Icon size="large" source={Microsoft365Colored} />}
    className="provider-accordion-item"
  >
    {onSendForwardingClick && (
      <Block marginBottom={withVar(SpacingToken.Spacing3)}>
        <Alert>
          <Text>
            You must be an Exchange or Global administrator in Microsoft 365 to set up forwarding. Not an administrator?{" "}
            <Link bold={false} onClick={onSendForwardingClick}>
              Send forwarding instructions to your organization&apos;s administrator.
            </Link>
          </Text>
        </Alert>
      </Block>
    )}
    <StepsList>
      <LiWithMargin>
        Access{" "}
        <Link bold target="_blank" href="https://admin.microsoft.com/AdminPortal/Home?ref=users">
          Active users
        </Link>{" "}
        in the Microsoft 365 Admin Center.
      </LiWithMargin>
      <LiWithMargin>
        <b>Select the name of the user</b> whose email address you want to forward, then select <b>the Mail tab</b>.
      </LiWithMargin>
      <LiWithMargin>
        Select <b>&quot;Manage email forwarding.&quot;</b>
      </LiWithMargin>
      <LiWithMargin>
        Select{" "}
        <Link bold target="_blank" href="https://security.microsoft.com/antispam">
          Manage outbound anti-spam policies
        </Link>
        .
      </LiWithMargin>
      <LiWithMargin>
        Select <b>&quot;Anti-spam outbound policy (Default)&quot;</b> settings.
      </LiWithMargin>
      <LiWithMargin>
        Select <b>&quot;Edit protection settings,&quot;</b> change the value in the &quot;Automatic forwarding&quot; to{" "}
        <b>&quot;On - Forwarding is enabled,&quot;</b> and save changes.
      </LiWithMargin>
      <LiWithMargin>
        Select <b>&quot;Forward all emails sent to this mailbox&quot;</b> and <b>enter</b> the HelpDesk&apos;s
        forwarding address.
        <ForwardingAddressCopy inboxAddress={inboxAddress} handleMailboxCopying={handleMailboxCopying} />
      </LiWithMargin>
      <LiWithMargin>
        <b>Save changes.</b>
      </LiWithMargin>
      <LiWithMargin>
        Optional: <b>Send a test message</b> or wait for the first email from a recipient.
      </LiWithMargin>
    </StepsList>
    <Block marginTop={withVar(SpacingToken.Spacing4)}>
      <Text>
        Learn more about{" "}
        <Link
          bold={false}
          target="_blank"
          href={HelpCenter.MICROSOFT_OFFICE_365_EMAIL_FORWARDING}
          onClick={onHelpCenterLinkClick}
        >
          Microsoft Office 365 email forwarding
        </Link>
        .
      </Text>
    </Block>
  </AccordionItem>
);
